import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/App';
import { 
  Users, Building2, CreditCard, TrendingUp, 
  Crown, Clock, AlertTriangle, RefreshCw,
  ChevronRight, Euro, Eye, LogIn, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const AdminDashboard = ({ onUpdate }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [coproprietes, setCoproprietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [deleting, setDeleting] = useState(null); // Pour l'état de suppression

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, subsRes, coproRes] = await Promise.all([
        apiClient.get('/admin/stats'),
        apiClient.get('/admin/users?limit=20'),
        apiClient.get('/admin/subscriptions'),
        apiClient.get('/admin/coproprietes')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setSubscriptions(subsRes.data.subscriptions);
      setCoproprietes(coproRes.data.coproprietes);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const accessCopropriete = async (coproId, coproNom) => {
    try {
      const response = await apiClient.post(`/admin/access-copropriete/${coproId}`);
      
      // Mettre à jour le token
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      // Recharger l'utilisateur
      const userResponse = await apiClient.get('/auth/me');
      if (onUpdate) {
        onUpdate(userResponse.data);
      }
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      
      toast.success(`Accès à "${coproNom}" activé`);
      navigate('/dashboard');
    } catch (error) {
      toast.error("Erreur lors de l'accès à la copropriété");
      console.error(error);
    }
  };

  // Suppression d'un utilisateur
  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userName}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    setDeleting(`user-${userId}`);
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      toast.success(`Utilisateur "${userName}" supprimé`);
      loadData(); // Recharger les données
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
      console.error(error);
    } finally {
      setDeleting(null);
    }
  };

  // Suppression d'une copropriété
  const deleteCopropriete = async (coproId, coproNom) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la copropriété "${coproNom}" ?\n\n⚠️ ATTENTION: Toutes les données associées seront définitivement supprimées :\n- Budgets, appels de fonds, factures\n- Assemblées générales, résolutions\n- Documents\n- Abonnements\n\nCette action est IRRÉVERSIBLE.`)) {
      return;
    }
    
    setDeleting(`copro-${coproId}`);
    try {
      await apiClient.delete(`/admin/coproprietes/${coproId}`);
      toast.success(`Copropriété "${coproNom}" supprimée`);
      loadData(); // Recharger les données
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
      console.error(error);
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      trial: 'bg-amber-100 text-amber-700',
      expired: 'bg-red-100 text-red-700',
      none: 'bg-slate-100 text-slate-600'
    };
    const labels = {
      active: 'Actif',
      trial: 'Essai',
      expired: 'Expiré',
      none: 'Aucun'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.none}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Crown className="w-7 h-7 text-amber-500" />
            Espace Master Admin
          </h1>
          <p className="text-slate-500 mt-1">Vue d'ensemble de la plateforme</p>
        </div>
        <Button 
          onClick={loadData} 
          variant="outline"
          className="flex items-center gap-2"
          data-testid="refresh-admin-stats"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </Button>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-users">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Utilisateurs</p>
                <p className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                <p className="text-xs text-emerald-600 mt-1">
                  +{stats?.recent_activity?.new_users_30d || 0} ce mois
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-coproprietes">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Copropriétés</p>
                <p className="text-3xl font-bold text-slate-900">{stats?.total_coproprietes || 0}</p>
                <p className="text-xs text-emerald-600 mt-1">
                  +{stats?.recent_activity?.new_coproprietes_30d || 0} ce mois
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-subscriptions">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Abonnements actifs</p>
                <p className="text-3xl font-bold text-slate-900">{stats?.subscriptions?.active || 0}</p>
                <p className="text-xs text-amber-600 mt-1">
                  {stats?.subscriptions?.trial || 0} en essai
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-revenue">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Revenus totaux</p>
                <p className="text-3xl font-bold text-slate-900">{stats?.revenue?.total || 0}€</p>
                <p className="text-xs text-slate-500 mt-1">
                  {stats?.revenue?.monthly_subscriptions || 0} mensuels · {stats?.revenue?.annual_subscriptions || 0} annuels
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Euro className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Vue d\'ensemble', icon: TrendingUp },
            { id: 'coproprietes', label: 'Copropriétés', icon: Building2 },
            { id: 'users', label: 'Utilisateurs', icon: Users },
            { id: 'subscriptions', label: 'Abonnements', icon: CreditCard }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Contenu des tabs */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Répartition des abonnements */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Répartition des abonnements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                    <span className="text-sm">Actifs</span>
                  </div>
                  <span className="font-semibold">{stats?.subscriptions?.active || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                    <span className="text-sm">En essai</span>
                  </div>
                  <span className="font-semibold">{stats?.subscriptions?.trial || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-sm">Expirés</span>
                  </div>
                  <span className="font-semibold">{stats?.subscriptions?.expired || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activité récente */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Activité des 30 derniers jours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="text-sm">Nouveaux utilisateurs</span>
                  </div>
                  <span className="font-semibold text-blue-600">+{stats?.recent_activity?.new_users_30d || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    <span className="text-sm">Nouvelles copropriétés</span>
                  </div>
                  <span className="font-semibold text-purple-600">+{stats?.recent_activity?.new_coproprietes_30d || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Onglet Copropriétés */}
      {activeTab === 'coproprietes' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Toutes les copropriétés ({coproprietes.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Copropriété</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Président</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Copropriétaires</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Abonnement</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coproprietes.map((copro) => (
                    <tr key={copro.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900">{copro.nom}</div>
                        <div className="text-xs text-slate-500 truncate max-w-xs">{copro.adresse}</div>
                      </td>
                      <td className="py-3 px-4">
                        {copro.president ? (
                          <div>
                            <div className="text-slate-900">{copro.president.prenom} {copro.president.nom}</div>
                            <div className="text-xs text-slate-500">{copro.president.email}</div>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-medium">{copro.nb_coproprietaires || 0}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(copro.subscription?.status)}
                      </td>
                      <td className="py-3 px-4 text-center space-x-2">
                        <Button
                          onClick={() => accessCopropriete(copro.id, copro.nom)}
                          size="sm"
                          variant="outline"
                          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        >
                          <LogIn className="w-4 h-4 mr-1" />
                          Accéder
                        </Button>
                        <Button
                          onClick={() => deleteCopropriete(copro.id, copro.nom)}
                          size="sm"
                          variant="outline"
                          disabled={deleting === `copro-${copro.id}`}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {coproprietes.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  Aucune copropriété trouvée
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'users' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Liste des utilisateurs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Utilisateur</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Email</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Copropriétés</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Abonnement</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Inscription</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900">{user.prenom} {user.nom}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{user.email}</td>
                      <td className="py-3 px-4 text-center">{user.nb_coproprietes || 0}</td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(user.subscription_status)}
                      </td>
                      <td className="py-3 px-4 text-slate-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {user.email?.toLowerCase() !== 'gmpdeveloppement@gmail.com' && (
                          <Button
                            onClick={() => deleteUser(user.id, `${user.prenom} ${user.nom}`)}
                            size="sm"
                            variant="outline"
                            disabled={deleting === `user-${user.id}`}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'subscriptions' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Liste des abonnements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Utilisateur</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Copropriété</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Plan</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Statut</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Expiration</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900">
                          {sub.user?.prenom} {sub.user?.nom}
                        </div>
                        <div className="text-xs text-slate-500">{sub.user?.email}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{sub.copropriete?.nom || '-'}</td>
                      <td className="py-3 px-4 text-center capitalize">{sub.plan || '-'}</td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(sub.status)}
                      </td>
                      <td className="py-3 px-4 text-slate-500">
                        {sub.subscription_end 
                          ? new Date(sub.subscription_end).toLocaleDateString('fr-FR')
                          : sub.trial_end 
                            ? new Date(sub.trial_end).toLocaleDateString('fr-FR')
                            : '-'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subscriptions.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  Aucun abonnement trouvé
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
